import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';
import { useTreasury } from '../contexts/TreasuryContext';
import { useNotifications } from '../contexts/NotificationContext';
import { TrendingUp, DollarSign, Trophy, Users, RefreshCw, Plus, Minus, ArrowUpRight, ArrowDownRight, Activity, Star, Zap, Vote, Eye, BarChart3, Settings, Bell } from 'lucide-react';

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected, address, balance } = useWallet();
  const { totalValue, suiStaked, usdcReserve, apy, refreshTreasury } = useTreasury();
  const { addNotification } = useNotifications();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showDepositForm, setShowDepositForm] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  
  // Enhanced user data with real calculations
  const [userData, setUserData] = useState({
    totalDeposits: 5000,
    totalEarnings: 1250.50,
    currentStake: 2500,
    membershipLevel: 'VIP',
    lastDeposit: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    performanceScore: 95,
    rank: 12
  });

  const portfolioStats = [
    { 
      label: 'Your Balance', 
      value: balance || '0 SUI', 
      icon: DollarSign,
      change: '+2.3%',
      changeType: 'positive'
    },
    { 
      label: 'Total Treasury', 
      value: `${(totalValue / 1000000).toFixed(1)}M SUI`, 
      icon: TrendingUp,
      change: '+5.1%',
      changeType: 'positive'
    },
    { 
      label: 'Your APY', 
      value: `${apy}%`, 
      icon: Trophy,
      change: '+0.2%',
      changeType: 'positive'
    },
    { 
      label: 'Membership', 
      value: userData.membershipLevel, 
      icon: Users,
      change: 'VIP',
      changeType: 'neutral'
    },
  ];

  const recentActivity = [
    { action: 'Staking Rewards', amount: '+125.5 SUI', time: '2 hours ago', type: 'reward' },
    { action: 'Racing Win', amount: '+2,340 SUI', time: '1 day ago', type: 'win' },
    { action: 'Governance Vote', amount: 'Voted FOR', time: '3 days ago', type: 'vote' },
    { action: 'Treasury Deposit', amount: '+1,000 SUI', time: '1 week ago', type: 'deposit' },
  ];

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshTreasury();
      setToastMessage('Data refreshed successfully!');
      setTimeout(() => setToastMessage(''), 3000);
    } catch (error) {
      setToastMessage('Failed to refresh data');
      setTimeout(() => setToastMessage(''), 3000);
    }
    setIsRefreshing(false);
  };

  const handleDeposit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      setToastMessage('Please enter a valid amount');
      setTimeout(() => setToastMessage(''), 3000);
      return;
    }

    const amount = parseFloat(depositAmount);
    if (amount < 10) {
      setToastMessage('Minimum deposit is 10 SUI');
      setTimeout(() => setToastMessage(''), 3000);
      return;
    }

    try {
      setIsRefreshing(true);
      // Simulate deposit processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update user data with math
      setUserData(prev => ({
        ...prev,
        totalDeposits: prev.totalDeposits + amount,
        currentStake: prev.currentStake + amount,
        lastDeposit: new Date()
      }));

      // Add notification
      addNotification({
        type: 'treasury',
        title: '💰 Deposit Successful',
        message: `Successfully deposited ${amount} SUI to the syndicate treasury.`,
        priority: 'medium',
        category: 'treasury',
        metadata: {
          amount: `${amount} SUI`
        }
      });

      setToastMessage(`Successfully deposited ${amount} SUI! Your stake is now ${userData.currentStake + amount} SUI.`);
      setDepositAmount('');
      setShowDepositForm(false);
      setTimeout(() => setToastMessage(''), 3000);
    } catch (error) {
      setToastMessage('Deposit failed. Please try again.');
      setTimeout(() => setToastMessage(''), 3000);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleWithdraw = async () => {
    const maxWithdraw = userData.currentStake * 0.5; // Max 50% withdrawal
    const withdrawAmount = Math.min(1000, maxWithdraw); // Simulate 1000 SUI withdrawal
    
    try {
      setIsRefreshing(true);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setUserData(prev => ({
        ...prev,
        currentStake: prev.currentStake - withdrawAmount
      }));

      addNotification({
        type: 'treasury',
        title: '💸 Withdrawal Processed',
        message: `Withdrawal of ${withdrawAmount} SUI has been processed.`,
        priority: 'medium',
        category: 'treasury',
        metadata: {
          amount: `${withdrawAmount} SUI`
        }
      });

      setToastMessage(`Withdrawal of ${withdrawAmount} SUI processed successfully!`);
      setTimeout(() => setToastMessage(''), 3000);
    } catch (error) {
      setToastMessage('Withdrawal failed. Please try again.');
      setTimeout(() => setToastMessage(''), 3000);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClaimRewards = async () => {
    const rewards = userData.currentStake * (apy / 100) / 12; // Monthly rewards
    
    try {
      setIsRefreshing(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setUserData(prev => ({
        ...prev,
        totalEarnings: prev.totalEarnings + rewards
      }));

      addNotification({
        type: 'treasury',
        title: '🎉 Rewards Claimed',
        message: `Claimed ${rewards.toFixed(2)} SUI in staking rewards!`,
        priority: 'high',
        category: 'treasury',
        metadata: {
          amount: `${rewards.toFixed(2)} SUI`
        }
      });

      setToastMessage(`Claimed ${rewards.toFixed(2)} SUI in rewards!`);
      setTimeout(() => setToastMessage(''), 3000);
    } catch (error) {
      setToastMessage('Failed to claim rewards. Please try again.');
      setTimeout(() => setToastMessage(''), 3000);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleVoteOnProposals = () => {
    addNotification({
      type: 'governance',
      title: '🗳️ Navigating to Governance',
      message: 'Taking you to the governance page to vote on proposals.',
      priority: 'medium',
      category: 'governance'
    });
    navigate('/governance');
  };

  const handleViewHorses = () => {
    addNotification({
      type: 'racing',
      title: '🏇 Viewing Horse Collection',
      message: 'Taking you to see our champion thoroughbreds.',
      priority: 'low',
      category: 'racing'
    });
    navigate('/horses');
  };

  const handleTreasuryDetails = () => {
    addNotification({
      type: 'treasury',
      title: '💰 Treasury Details',
      message: 'Opening detailed treasury information.',
      priority: 'low',
      category: 'treasury'
    });
    navigate('/treasury');
  };

  const handleViewAnalytics = () => {
    addNotification({
      type: 'system',
      title: '📊 Analytics Dashboard',
      message: 'Opening advanced analytics and insights.',
      priority: 'low',
      category: 'system'
    });
    navigate('/analytics');
  };

  const handleOpenSettings = () => {
    addNotification({
      type: 'system',
      title: '⚙️ Settings',
      message: 'Opening your account settings.',
      priority: 'low',
      category: 'system'
    });
    navigate('/settings');
  };

  const handleViewNotifications = () => {
    // This will be handled by the notification center component
    setToastMessage('Click the bell icon in the top navigation to view notifications');
    setTimeout(() => setToastMessage(''), 3000);
  };

  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔐</div>
          <h1 className="text-2xl font-bold text-white mb-4">Wallet Not Connected</h1>
          <p className="text-slate-400 mb-6">Please connect your wallet to view your dashboard</p>
          <Link
            to="/auth"
            className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200"
          >
            Connect Wallet
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 bg-slate-800 border border-slate-700 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-slideIn">
          {toastMessage}
        </div>
      )}
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
            <p className="text-slate-400">Welcome back, {address?.slice(0, 6)}...{address?.slice(-4)}</p>
          </div>
                 <div className="flex flex-wrap gap-3">
                   <button
                     onClick={handleRefresh}
                     disabled={isRefreshing}
                     className="flex items-center space-x-2 bg-slate-700 text-white px-4 py-2 rounded-lg hover:bg-slate-600 transition-colors disabled:opacity-50"
                   >
                     <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                     <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
                   </button>
                   <button
                     onClick={() => setShowDepositForm(!showDepositForm)}
                     className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200"
                   >
                     <Plus className="w-4 h-4" />
                     <span>Deposit</span>
                   </button>
                   <button
                     onClick={handleWithdraw}
                     disabled={isRefreshing}
                     className="flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-200 disabled:opacity-50"
                   >
                     <Minus className="w-4 h-4" />
                     <span>Withdraw</span>
                   </button>
                   <button
                     onClick={handleClaimRewards}
                     disabled={isRefreshing}
                     className="flex items-center space-x-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-4 py-2 rounded-lg hover:from-yellow-600 hover:to-yellow-700 transition-all duration-200 disabled:opacity-50"
                   >
                     <Trophy className="w-4 h-4" />
                     <span>Claim Rewards</span>
                   </button>
                 </div>
        </div>

        {/* Deposit Form */}
        {showDepositForm && (
          <div className="mb-8 bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Deposit to Treasury</h3>
            <form onSubmit={handleDeposit} className="flex space-x-4">
              <div className="flex-1">
                <input
                  type="number"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder="Enter amount in SUI"
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none"
                  step="0.01"
                  min="0"
                />
              </div>
              <button
                type="submit"
                className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200"
              >
                Deposit
              </button>
              <button
                type="button"
                onClick={() => setShowDepositForm(false)}
                className="bg-slate-700 text-white px-6 py-3 rounded-lg hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
            </form>
          </div>
        )}

        {/* Portfolio Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {portfolioStats.map((stat, index) => (
            <div key={index} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 hover:border-cyan-500/50 transition-all duration-200 group">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-cyan-500/20 rounded-lg group-hover:bg-cyan-500/30 transition-colors">
                  <stat.icon className="w-6 h-6 text-cyan-400" />
                </div>
                <div className="flex items-center space-x-2">
                  {stat.changeType === 'positive' && <ArrowUpRight className="w-4 h-4 text-green-400" />}
                  {stat.changeType === 'negative' && <ArrowDownRight className="w-4 h-4 text-red-400" />}
                  <span className={`text-xs font-medium ${
                    stat.changeType === 'positive' ? 'text-green-400' :
                    stat.changeType === 'negative' ? 'text-red-400' : 'text-slate-400'
                  }`}>
                    {stat.change}
                  </span>
                </div>
              </div>
              <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-slate-400 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Enhanced User Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-500/20 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
              <span className="text-green-400 text-sm font-medium">+{userData.totalEarnings.toFixed(2)} SUI</span>
            </div>
            <div className="text-2xl font-bold text-white mb-1">{userData.totalEarnings.toFixed(2)} SUI</div>
            <div className="text-slate-400 text-sm">Total Earnings</div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Activity className="w-6 h-6 text-blue-400" />
              </div>
              <span className="text-blue-400 text-sm font-medium">#{userData.rank}</span>
            </div>
            <div className="text-2xl font-bold text-white mb-1">{userData.performanceScore}%</div>
            <div className="text-slate-400 text-sm">Performance Score</div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Star className="w-6 h-6 text-purple-400" />
              </div>
              <span className="text-purple-400 text-sm font-medium">{userData.membershipLevel}</span>
            </div>
            <div className="text-2xl font-bold text-white mb-1">{userData.currentStake.toFixed(0)} SUI</div>
            <div className="text-slate-400 text-sm">Current Stake</div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Recent Activity */}
          <div className="lg:col-span-2">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-white mb-6">Recent Activity</h2>
              <div className="space-y-4">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className={`w-3 h-3 rounded-full ${
                        activity.type === 'reward' ? 'bg-green-500' :
                        activity.type === 'win' ? 'bg-yellow-500' :
                        activity.type === 'vote' ? 'bg-blue-500' :
                        'bg-cyan-500'
                      }`}></div>
                      <div>
                        <div className="text-white font-medium">{activity.action}</div>
                        <div className="text-slate-400 text-sm">{activity.time}</div>
                      </div>
                    </div>
                    <div className={`font-semibold ${
                      activity.type === 'reward' || activity.type === 'win' || activity.type === 'deposit' 
                        ? 'text-green-400' : 'text-blue-400'
                    }`}>
                      {activity.amount}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div>
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6 mb-6">
              <h2 className="text-xl font-semibold text-white mb-6">Quick Actions</h2>
              <div className="space-y-3">
                <button
                  onClick={handleVoteOnProposals}
                  className="flex items-center justify-center space-x-2 w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-3 px-4 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200"
                >
                  <Vote className="w-4 h-4" />
                  <span>Vote on Proposals</span>
                </button>
                <button
                  onClick={handleViewHorses}
                  className="flex items-center justify-center space-x-2 w-full border border-cyan-500 text-cyan-400 py-3 px-4 rounded-lg hover:bg-cyan-500/10 transition-all duration-200"
                >
                  <Eye className="w-4 h-4" />
                  <span>View Horses</span>
                </button>
                <button
                  onClick={handleTreasuryDetails}
                  className="flex items-center justify-center space-x-2 w-full border border-cyan-500 text-cyan-400 py-3 px-4 rounded-lg hover:bg-cyan-500/10 transition-all duration-200"
                >
                  <BarChart3 className="w-4 h-4" />
                  <span>Treasury Details</span>
                </button>
                <button
                  onClick={handleViewAnalytics}
                  className="flex items-center justify-center space-x-2 w-full border border-green-500 text-green-400 py-3 px-4 rounded-lg hover:bg-green-500/10 transition-all duration-200"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span>Analytics</span>
                </button>
                <button
                  onClick={handleOpenSettings}
                  className="flex items-center justify-center space-x-2 w-full border border-purple-500 text-purple-400 py-3 px-4 rounded-lg hover:bg-purple-500/10 transition-all duration-200"
                >
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </button>
              </div>
            </div>

            {/* Treasury Overview */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">Treasury Overview</h2>
                <button
                  onClick={handleTreasuryDetails}
                  className="flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm"
                >
                  <BarChart3 className="w-4 h-4" />
                  <span>View Details</span>
                </button>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">SUI Staked</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-white font-medium">{(suiStaked / 1000000).toFixed(1)}M</span>
                    <ArrowUpRight className="w-4 h-4 text-green-400" />
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">USDC Reserve</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-white font-medium">${(usdcReserve / 1000000).toFixed(1)}M</span>
                    <ArrowUpRight className="w-4 h-4 text-green-400" />
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Current APY</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-green-400 font-medium">{apy}%</span>
                    <ArrowUpRight className="w-4 h-4 text-green-400" />
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Your Share</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-cyan-400 font-medium">
                      {((userData.currentStake / totalValue) * 100).toFixed(2)}%
                    </span>
                    <Star className="w-4 h-4 text-cyan-400" />
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-700">
                <button
                  onClick={handleTreasuryDetails}
                  className="w-full bg-slate-700 text-white py-2 px-4 rounded-lg hover:bg-slate-600 transition-colors text-sm"
                >
                  View Full Treasury Report
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
